import { Box, Chip, Grid, Stack, Typography } from "@mui/material";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import { useFormContext, useWatch } from "react-hook-form";
import { ChoiceField, SellingField, StepHeading } from "./FormFields";
import { returnPolicies, SellingValues } from "./types";
import { parsePriceCents } from "./pricing";

export default function ShippingStep() {
  const { control } = useFormContext<SellingValues>();
  const shipping = useWatch({ control, name: "shipping" });
  return (
    <>
      <StepHeading title="Let's Get It to Its New Home">
        Shipping within the United States. Set a flat charge and measure your
        packed toy.
      </StepHeading>
      <Stack spacing={3}>
        <Box>
          <SellingField
            name="shipping"
            label="Flat U.S. shipping charge (USD)"
            required
            money
            suffix="USD"
            helper="Enter 0.00 to offer free shipping. This is your charge, not a carrier quote."
          />
          {parsePriceCents(shipping, true) === 0 && (
            <Chip
              icon={<LocalShippingOutlined />}
              label="Free Shipping"
              color="success"
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        <Box>
          <Typography fontWeight={650} color="secondary.main" sx={{ mb: 0.5 }}>
            Packed box measurements
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Include the toy, padding, and shipping box. Enter dimensions in
            inches and total packed weight in pounds and ounces.
          </Typography>
          <Grid container spacing={2}>
            {(["length", "width", "height"] as const).map((name) => (
              <Grid key={name} item xs={12} sm={4}>
                <SellingField
                  name={name}
                  label={name[0].toUpperCase() + name.slice(1)}
                  required
                  numeric
                  suffix="in"
                />
              </Grid>
            ))}
            <Grid item xs={6}>
              <SellingField
                name="pounds"
                label="Weight: pounds"
                numeric
                suffix="lb"
                helper="Whole pounds; use 0 for a lighter box."
              />
            </Grid>
            <Grid item xs={6}>
              <SellingField
                name="ounces"
                label="Weight: ounces"
                numeric
                suffix="oz"
                helper="Additional ounces, less than 16."
              />
            </Grid>
          </Grid>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Carrier rates, labels, tracking, and delivery estimates are not
          connected yet.
        </Typography>
        <ChoiceField
          name="returns"
          label="What's Your Return Policy?"
          options={Object.entries(returnPolicies).map(([value, policy]) => ({
            value,
            ...policy,
          }))}
        />
        <Typography variant="caption" color="text.secondary">
          These are preliminary seller preferences. Final marketplace return
          terms will be confirmed before publishing is enabled.
        </Typography>
      </Stack>
    </>
  );
}
